import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { onboardingApi } from "@/lib/api/services/onboarding.api";
import { userApi } from "@/lib/api/services/user.api";
import { useOnboarding } from "@/features/onboarding/OnboardingContext";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/(public)/onboarding/plan")({
  component: ChoosePlanPage,
});

interface Plan {
  id: string;
  name: MessageDescriptor;
  price: MessageDescriptor;
  description: MessageDescriptor;
  features: MessageDescriptor[];
  recommended?: boolean;
}

const PLANS: Plan[] = [
  {
    id: "standard",
    name: msg`Standard`,
    price: msg`$20/month`,
    description: msg`For single locations`,
    features: [msg`Up to 1 location`, msg`Recipe management`, msg`Ingredient catalog`, msg`Basic analytics`],
  },
  {
    id: "professional",
    name: msg`Professional`,
    price: msg`$20/month`,
    description: msg`For growing restaurant groups`,
    features: [msg`Up to 10 locations`, msg`Everything in Standard`, msg`Advanced analytics`, msg`Team management`],
    recommended: true,
  },
  {
    id: "enterprise",
    name: msg`Enterprise`,
    price: msg`$20/month`,
    description: msg`For large organizations`,
    features: [msg`Unlimited locations`, msg`Everything in Professional`, msg`Custom integrations`, msg`Dedicated support`],
  },
];

function ChoosePlanPage() {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const { refetchUser } = useAuth();
  const {
    orgName, orgCountry, orgTimezone,
    companyNames, regionsByCompany, locationsByRegion, invites,
    clearOnboarding,
  } = useOnboarding();

  const [selectedPlan, setSelectedPlan] = useState("standard");
  const [isCreating, setIsCreating] = useState(false);

  const handleStart = async () => {
    if (!orgName.trim()) {
      toast.error(t`Please go back and enter an organization name first`);
      return;
    }

    setIsCreating(true);
    try {
      const companies = companyNames.length > 0 ? companyNames : [orgName];
      await onboardingApi.completeOnboarding({
        name: orgName,
        country: orgCountry || undefined,
        timezone: orgTimezone || undefined,
        companies: companies.map((companyName) => ({
          name: companyName,
          regions: (regionsByCompany[companyName] ?? []).map((regionName) => ({
            name: regionName,
            locations: locationsByRegion[regionName] ?? [],
          })),
        })),
      });

      // Invite users (separate requests — invite failures should not block onboarding)
      if (invites.length > 0) {
        await Promise.allSettled(
          invites.map((invite) =>
            userApi.create({
              email: invite.email,
              firstName: "Invited",
              lastName: "User",
              roleId: invite.roleId,
              sendInviteEmail: true,
              forcePasswordChange: true,
            }),
          ),
        );
      }

      clearOnboarding();
      await refetchUser();
      navigate({ to: "/dashboard" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t`Failed to complete setup`);
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold leading-tight text-(--brand-black)" style={{ fontSize: "2rem" }}>
          {t`Choose your plan`}
        </h1>
        <p className="text-sm leading-relaxed text-(--gray-500)">
          {t`Start managing recipes, kitchens, and teams across your organization. You can upgrade, downgrade, or cancel anytime.`}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {PLANS.map((plan) => {
          const isSelected = selectedPlan === plan.id;
          return (
            <button
              key={plan.id}
              type="button"
              onClick={() => setSelectedPlan(plan.id)}
              disabled={isCreating}
              className={cn(
                "w-full text-left rounded-md p-4 transition-shadow disabled:opacity-50 bg-(--brand-white)",
                isSelected
                  ? "border-[1.5px] border-(--brand-black)"
                  : "border border-(--gray-200)",
              )}
              style={{
                boxShadow: isSelected ? "0 0 0 3px rgba(35,31,31,0.06)" : "none",
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-(--brand-black)">{i18n._(plan.name)}</span>
                    {plan.recommended && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-(--color-success-bg) text-(--color-success-text)">
                        {t`Recommended`}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-(--gray-500)">{i18n._(plan.description)}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold text-(--brand-black)">{i18n._(plan.price)}</span>
                  <div
                    className={cn(
                      "w-5 h-5 rounded flex items-center justify-center shrink-0",
                      isSelected
                        ? "bg-(--brand-black)"
                        : "bg-(--brand-white) border-[1.5px] border-(--gray-300)",
                    )}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {plan.features.map((feature) => (
                  <span key={feature.id} className="text-xs px-2.5 py-1 rounded-full bg-(--gray-100) text-(--gray-600)">
                    {i18n._(feature)}
                  </span>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleStart}
          disabled={isCreating}
          className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-md transition-opacity hover:opacity-90 disabled:opacity-70 disabled:cursor-not-allowed bg-(--brand-black)"
        >
          {isCreating
            ? <><Loader2 className="w-4 h-4 animate-spin" /> {t`Setting up your workspace...`}</>
            : t`Start your journey`
          }
        </button>
        <button
          type="button"
          onClick={handleStart}
          disabled={isCreating}
          className="text-sm font-medium text-center transition-opacity hover:opacity-70 disabled:opacity-50 text-(--gray-600)"
        >
          {t`Skip for now`}
        </button>
      </div>
    </div>
  );
}
